<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import clockIcon from '@/shared/assets/icons/ClockIcon.svg';
  import { useDashboardStore } from '../../model/dashboard.store';
  import type { DashboardLeadTimeEntry } from '../../model/dashboard.types';
  import { formatDays, formatVariationPct } from '../../model/format';
  import { formatPeriodLabel } from '../../model/period';
  import StageDurationBars, { type StageDuration } from '../StageDurationBars.vue';
  import VariationBadge from '../VariationBadge.vue';
  import WidgetCard from '../WidgetCard.vue';

  interface LeadTimeSection {
    title: string;
    stages: StageDuration[];
    totalLabel: string;
    variationPct: number | null;
    badgeText?: string;
    isEmpty: boolean;
  }

  const dashboardStore = useDashboardStore();
  const { data, isLoading, comparisonLabel, granularity, period } = storeToRefs(dashboardStore);

  const periodLabel = computed(() => formatPeriodLabel(granularity.value, period.value));

  function buildBadgeText(variationPct: number | null): string | undefined {
    if (variationPct === null || variationPct === 0) return undefined;
    const direction = variationPct < 0 ? 'mais rápido' : 'mais devagar';
    return `${formatVariationPct(Math.abs(variationPct))} ${direction}`;
  }

  function buildSection(entry: DashboardLeadTimeEntry | undefined, title: string): LeadTimeSection {
    const isEmpty = (entry?.totalDays ?? 0) <= 0;
    const variationPct = isEmpty ? null : (entry?.variationPct ?? null);
    return {
      title,
      isEmpty,
      stages: [
        { label: 'Envio → Aprovação', days: entry?.sendToApprovalDays ?? 0 },
        { label: 'Aprovação → Verificação', days: entry?.approvalToVerificationDays ?? 0 },
      ],
      totalLabel: `Tempo médio de Homologação: ${formatDays(entry?.totalDays ?? 0)}`,
      variationPct,
      badgeText: buildBadgeText(variationPct),
    };
  }

  const sections = computed<LeadTimeSection[]>(() => [
    buildSection(data.value?.homologationLeadTime.documents, 'Documentos'),
    buildSection(data.value?.homologationLeadTime.records, 'Registros'),
  ]);
</script>

<template>
  <WidgetCard
    title="Tempo médio entre etapas da Homologação"
    :icon="clockIcon"
    :period-label="periodLabel"
    :loading="isLoading"
    :has-data="data !== null"
    skeleton="stage-bars"
  >
    <div
      data-testid="homologation-lead-time-widget"
      class="flex h-full flex-col"
    >
      <section
        v-for="(section, index) in sections"
        :key="section.title"
        data-testid="lead-time-section"
        class="flex min-h-0 flex-1 flex-col justify-center gap-5"
        :class="index === 0 ? 'border-b border-gray-02 pb-5' : 'pt-5'"
      >
        <h3 class="text-sm font-normal text-gray-07">
          {{ section.title }}
        </h3>
        <template v-if="!section.isEmpty">
          <StageDurationBars :stages="section.stages" />
          <div class="flex flex-col gap-2">
            <VariationBadge
              :variation-pct="section.variationPct"
              :text="section.badgeText"
              invert
              :comparison-label="comparisonLabel"
            />
            <p class="text-sm text-gray-03">
              {{ section.totalLabel }}
            </p>
          </div>
        </template>
        <p
          v-else
          data-testid="lead-time-empty"
          class="flex h-[86px] items-center justify-center text-sm text-gray-03"
        >
          Nenhuma homologação no período
        </p>
      </section>
    </div>
  </WidgetCard>
</template>
